import { pick } from 'ramda';
import { useMemo, useCallback } from 'react';
import { useComponentProps } from 'js/hooks/component/useComponentProps';
import { fn, required, range, money } from 'js/utils/Validation/validationRules';

import { useDynamicReducerSelector } from '../store';

export const useValidationConfig = ({ translate }) => {
    const {
        config,
        limit: { amount: limitAmount },
        amountToBeLeftOnBalance: { amount: amountToBeLeft }
    } = useDynamicReducerSelector(pick([
        'config', 'limit', 'amountToBeLeftOnBalance'
    ]));
    const { tkPrefix } = useComponentProps();
    const { minWithdrawAmount, minLimitValue = 0 } = config;
    const minLimit = Math.max(1, ...[minLimitValue, minWithdrawAmount].filter(Number));

    const validateDiff = useCallback((val1, val2) => {
        const value1 = String(val1).replace(',', '.');
        const value2 = String(val2).replace(',', '.');

        if (!value1 || !value2) {
            return null;
        }

        if (Number(value1) - Number(value2) < minLimitValue) {
            return [translate('field.error.diff', [minLimitValue])];
        }

        return null;
    }, [translate, minLimitValue]);

    return useMemo(() => ({
        limit: [
            required({ error: `${tkPrefix}field.required` }),
            money(),
            range({ min: minLimit, max: 999999999, error: translate('limit.error.range', [minLimit]) }),
            fn({ fn: (value) => validateDiff(value, amountToBeLeft) })
        ],
        amountToBeLeftOnBalance: [
            required({ error: `${tkPrefix}field.required` }),
            money({ isZeroAccepted: true }),
            range({ min: 0, max: 999999999, error: `${tkPrefix}amountToBeLeftOnBalance.error.range` }),
            fn({ fn: (value) => validateDiff(limitAmount, value) })
        ]
    }), [minLimit, translate, tkPrefix, limitAmount, amountToBeLeft, validateDiff]);
};
